import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link as RouterLink } from "react-router-dom";
import { appConstants } from "../_constants/app.constants";
import {
  IconCalendar,
  IconChevronRight,
  IconStar,
  IconTrophy,
  IconUsers,
} from "../ui";

function pubField(t, camel, pascal) {
  const v = t?.[camel] ?? t?.[pascal];
  if (v === null || v === undefined) return "";
  return typeof v === "string" ? v.trim() : v;
}

function fmtShortDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("es", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function hubPath(t) {
  const slug = pubField(t, "slug", "Slug");
  const id = t.id ?? t.Id;
  if (slug) return `/tournamentactions/${encodeURIComponent(slug)}`;
  return `/tournamentactions/${id}`;
}

function stripTime(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function sameDay(a, b) {
  if (!a || !b) return false;
  const da = new Date(a);
  const db = new Date(b);
  if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) return false;
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

function isFutureDay(iso, todayRef) {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return stripTime(d).getTime() > stripTime(todayRef).getTime();
}

function daysUntil(iso, todayRef) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const diffMs = stripTime(d).getTime() - stripTime(todayRef).getTime();
  return Math.round(diffMs / 86400000);
}

function pickField(match) {
  return match?.fieldName ?? match?.FieldName ?? "";
}

function pickActiveMatch(matches) {
  if (!Array.isArray(matches) || matches.length === 0) return null;
  const active = matches.find((m) => (m.isActive ?? m.IsActive) === true);
  if (active) return active;
  return matches[0] ?? null;
}

function deriveStage(detail) {
  if (!detail) return "";
  const groupCount = detail.groupCount ?? detail.GroupCount ?? 0;
  if (groupCount > 0) return "Grupos";
  return "Fase final";
}

const LiveDot = ({ size = 8 }) => (
  <span className="relative inline-flex" style={{ height: size, width: size }}>
    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
    <span className="relative inline-flex h-full w-full rounded-full bg-red-500" />
  </span>
);

function LivePill({ compact = false }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full bg-red-500 ${
        compact ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]"
      } font-extrabold uppercase tracking-widest text-white shadow ring-1 ring-red-300/40`}
    >
      <LiveDot size={compact ? 7 : 8} />
      EN VIVO
    </span>
  );
}

function UpcomingPill({ daysAhead, compact = false }) {
  let label = "PRÓXIMO";
  if (typeof daysAhead === "number") {
    if (daysAhead === 1) label = "MAÑANA";
    else if (daysAhead > 1 && daysAhead <= 7) label = `EN ${daysAhead} DÍAS`;
    else if (daysAhead > 7) label = "PRÓXIMAMENTE";
  }
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full bg-gold-bright ${
        compact ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]"
      } font-extrabold uppercase tracking-widest text-arena shadow ring-1 ring-gold/40`}
    >
      <span
        className={compact ? "h-1.5 w-1.5" : "h-2 w-2"}
        style={{ display: "inline-block", borderRadius: "9999px", background: "#0f3531" }}
      />
      {label}
    </span>
  );
}

function FieldArt() {
  return (
    <div className="pointer-events-none absolute inset-0 opacity-[0.22]" aria-hidden>
      <svg
        viewBox="0 0 400 220"
        preserveAspectRatio="xMidYMid slice"
        className="h-full w-full"
      >
        <rect
          x="6"
          y="6"
          width="388"
          height="208"
          fill="none"
          stroke="rgba(255,255,255,0.55)"
          strokeWidth="1.5"
        />
        <line
          x1="200"
          y1="6"
          x2="200"
          y2="214"
          stroke="rgba(255,255,255,0.55)"
          strokeWidth="1.5"
        />
        <circle
          cx="200"
          cy="110"
          r="32"
          fill="none"
          stroke="rgba(255,255,255,0.55)"
          strokeWidth="1.5"
        />
        <rect
          x="6"
          y="64"
          width="44"
          height="92"
          fill="none"
          stroke="rgba(255,255,255,0.45)"
          strokeWidth="1.5"
        />
        <rect
          x="350"
          y="64"
          width="44"
          height="92"
          fill="none"
          stroke="rgba(255,255,255,0.45)"
          strokeWidth="1.5"
        />
      </svg>
    </div>
  );
}

function MatchScore({ match, large = false }) {
  if (!match) return null;
  const local = match.localTeamName ?? match.LocalTeamName ?? "—";
  const away = match.foreignTeamName ?? match.ForeignTeamName ?? "—";
  const lg = match.localGoals ?? match.LocalGoals ?? 0;
  const fg = match.foreignGoals ?? match.ForeignGoals ?? 0;

  if (large) {
    return (
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-widest text-white/55">
            LOCAL
          </p>
          <p className="truncate font-display text-lg font-bold text-white sm:text-xl">
            {local}
          </p>
        </div>
        <div className="text-center font-display text-3xl font-extrabold leading-none text-gold-bright sm:text-4xl">
          <span>{lg}</span>
          <span className="px-2 text-white/40">–</span>
          <span>{fg}</span>
        </div>
        <div className="min-w-0 text-right">
          <p className="text-[10px] font-bold uppercase tracking-widest text-white/55">
            VISITANTE
          </p>
          <p className="truncate font-display text-lg font-bold text-white sm:text-xl">
            {away}
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 text-center">
      <span className="truncate font-bold text-ink" title={local}>
        {local}
      </span>
      <span className="font-display text-base font-extrabold text-arena-bright">
        {lg}
        <span className="px-1.5 text-ink-muted/70">·</span>
        {fg}
      </span>
      <span className="truncate font-bold text-ink" title={away}>
        {away}
      </span>
    </div>
  );
}

function FavoriteButton({ tournament, user, favorite, onFavorite, tone = "dark" }) {
  if (!onFavorite) return null;
  const dark = tone === "dark";
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onFavorite?.(tournament);
      }}
      disabled={!user}
      title={
        user
          ? favorite
            ? "Quitar favorito"
            : "Marcar favorito"
          : "Inicia sesión para guardar"
      }
      className={`focus-ring flex h-9 w-9 items-center justify-center rounded-xl border shadow-md backdrop-blur-md transition disabled:cursor-not-allowed disabled:opacity-40 ${
        dark
          ? "border-white/25 bg-ink/30 text-white hover:bg-ink/50"
          : "border-arena/15 bg-white/85 text-arena hover:bg-white"
      }`}
      aria-label={favorite ? "Quitar favorito" : "Marcar favorito"}
    >
      <IconStar className={favorite ? "text-gold-bright drop-shadow" : ""} />
    </button>
  );
}

function FeaturedLiveCard({
  tournament: t,
  extras,
  user,
  favorite,
  onFavorite,
}) {
  const name = pubField(t, "name", "Name") || "Torneo";
  const sport = pubField(t, "sportType", "SportType");
  const fmt = pubField(t, "format", "Format");
  const start = t.startDate ?? t.StartDate;
  const teamCount = extras?.detail?.teamCount ?? extras?.detail?.TeamCount ?? 0;
  const stage = extras?.stage;
  const activeMatch = extras?.activeMatch;
  const tagline = [sport, fmt && fmt !== "—" ? fmt : null].filter(Boolean).join(" · ");
  const field = pickField(activeMatch);

  return (
    <article className="relative animate-fade-up overflow-hidden rounded-[1.6rem] border border-arena/15 bg-gradient-to-br from-[#06251f] via-[#0c3530] to-[#041815] text-white shadow-[0_30px_60px_-30px_rgba(7,21,18,0.6)] ring-1 ring-black/30">
      <FieldArt />
      <div className="relative grid gap-0 md:grid-cols-5">
        <div className="relative flex flex-col justify-between gap-8 p-5 sm:p-7 md:col-span-2">
          <div className="flex items-center justify-between">
            <LivePill />
            <FavoriteButton
              tournament={t}
              user={user}
              favorite={favorite}
              onFavorite={onFavorite}
              tone="dark"
            />
          </div>
          <div>
            {tagline ? (
              <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.22em] text-gold-bright/90">
                {tagline}
              </p>
            ) : null}
            <h3 className="font-display text-3xl font-extrabold leading-tight drop-shadow-md sm:text-[2.1rem]">
              {name}
            </h3>
          </div>
        </div>

        <div className="relative bg-black/20 p-5 ring-1 ring-white/5 sm:p-7 md:col-span-3">
          <header className="flex items-center justify-between text-[11px] font-bold uppercase tracking-widest text-white/75">
            <span>PARTIDO EN CURSO</span>
            {field ? (
              <span className="inline-flex items-center gap-1.5 text-white/70">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-gold-bright" />
                {field}
              </span>
            ) : null}
          </header>

          <div className="mt-3 rounded-2xl bg-black/35 p-4 ring-1 ring-white/10">
            {activeMatch ? (
              <MatchScore match={activeMatch} large />
            ) : (
              <p className="text-sm text-white/70">
                Aún no hay un partido en curso. Entra para ver el calendario del día.
              </p>
            )}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-white/80">
            {teamCount > 0 ? (
              <span className="inline-flex items-center gap-1.5">
                <IconUsers size={16} className="text-white/60" />
                {teamCount} equipos
              </span>
            ) : null}
            {stage ? (
              <span className="inline-flex items-center gap-1.5">
                <IconTrophy size={14} className="text-white/60" />
                {stage}
              </span>
            ) : null}
            {start ? (
              <span className="inline-flex items-center gap-1.5">
                <IconCalendar size={14} className="text-white/60" />
                {fmtShortDate(start)}
              </span>
            ) : null}
          </div>

          <RouterLink
            to={hubPath(t)}
            className="focus-ring mt-5 inline-flex w-full items-center justify-between rounded-2xl bg-gradient-to-br from-gold-bright to-gold px-5 py-3.5 font-display text-base font-bold text-arena shadow-lg ring-1 ring-gold/35 transition hover:shadow-glow"
          >
            Entrar al torneo en vivo
            <IconChevronRight size={18} />
          </RouterLink>
        </div>
      </div>
    </article>
  );
}

function CompactTournamentCard({
  tournament: t,
  extras,
  user,
  favorite,
  onFavorite,
  variant = "live",
  todayRef,
}) {
  const name = pubField(t, "name", "Name") || "Torneo";
  const sport = pubField(t, "sportType", "SportType");
  const fmt = pubField(t, "format", "Format");
  const start = t.startDate ?? t.StartDate;
  const teamCount = extras?.detail?.teamCount ?? extras?.detail?.TeamCount ?? 0;
  const stage = extras?.stage;
  const activeMatch = extras?.activeMatch;
  const tagline = [sport, fmt && fmt !== "—" ? fmt : null]
    .filter(Boolean)
    .join(" · ");
  const field = pickField(activeMatch);
  const isLive = variant === "live";
  const days = !isLive && todayRef ? daysUntil(start, todayRef) : null;

  return (
    <article className="relative flex animate-fade-up flex-col overflow-hidden rounded-2xl border border-arena/15 bg-white shadow-[0_18px_42px_-18px_rgba(7,21,18,0.4)] ring-1 ring-black/[0.03] transition hover:-translate-y-0.5 hover:shadow-[0_22px_44px_-18px_rgba(7,21,18,0.5)]">
      <div className="relative bg-gradient-to-br from-[#06251f] via-[#0e3a34] to-[#041815] p-4 text-white sm:p-5">
        <FieldArt />
        <div className="relative flex items-start justify-between">
          {isLive ? <LivePill compact /> : <UpcomingPill compact daysAhead={days} />}
          <FavoriteButton
            tournament={t}
            user={user}
            favorite={favorite}
            onFavorite={onFavorite}
            tone="dark"
          />
        </div>
        <div className="relative mt-8 sm:mt-10">
          {tagline ? (
            <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.22em] text-gold-bright/90">
              {tagline}
            </p>
          ) : null}
          <h3 className="font-display text-xl font-extrabold leading-tight drop-shadow-md sm:text-2xl">
            {name}
          </h3>
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-3 p-4 sm:p-5">
        <div className="rounded-xl bg-cream/70 px-3 py-2.5 ring-1 ring-arena/10">
          {isLive ? (
            <>
              <header className="mb-1.5 flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-ink-muted">
                <span>PARTIDO EN CURSO</span>
                {field ? (
                  <span className="inline-flex items-center gap-1 text-ink-soft">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-gold" />
                    {field}
                  </span>
                ) : null}
              </header>
              {activeMatch ? (
                <MatchScore match={activeMatch} />
              ) : (
                <p className="text-sm text-ink-muted">
                  Sin partido en curso ahora mismo.
                </p>
              )}
            </>
          ) : (
            <>
              <header className="mb-1.5 flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-ink-muted">
                <span>COMIENZA</span>
                {typeof days === "number" ? (
                  <span className="inline-flex items-center gap-1 text-ink-soft">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-gold" />
                    {days === 0
                      ? "Hoy"
                      : days === 1
                      ? "En 1 día"
                      : `En ${days} días`}
                  </span>
                ) : null}
              </header>
              <p className="text-center font-display text-base font-bold text-ink">
                {start ? fmtShortDate(start) : "Fecha por definir"}
              </p>
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-ink-muted">
          {teamCount > 0 ? (
            <span className="inline-flex items-center gap-1">
              <IconUsers size={14} className="text-arena" />
              {teamCount} equipos
            </span>
          ) : null}
          {stage ? (
            <span className="inline-flex items-center gap-1">
              <IconTrophy size={12} className="text-arena" />
              {stage}
            </span>
          ) : null}
          {start ? (
            <span className="inline-flex items-center gap-1">
              <IconCalendar size={12} className="text-arena" />
              {fmtShortDate(start)}
            </span>
          ) : null}
        </div>

        <RouterLink
          to={hubPath(t)}
          className="focus-ring mt-auto inline-flex items-center justify-between rounded-xl bg-arena px-4 py-2.5 font-bold text-cream shadow ring-1 ring-arena/30 transition hover:bg-arena-bright"
        >
          Entrar al torneo
          <IconChevronRight size={16} />
        </RouterLink>
      </div>
    </article>
  );
}

export default function TournamentBoard({
  tournaments,
  serverNow,
  user,
  isFavorite,
  onFavorite,
  emptyState = null,
}) {
  const todayRef = useMemo(() => serverNow ?? new Date(), [serverNow]);

  const live = useMemo(
    () =>
      tournaments.filter(
        (t) =>
          sameDay(t.startDate ?? t.StartDate, todayRef) &&
          !(t.isFinished ?? t.IsFinished)
      ),
    [tournaments, todayRef]
  );

  const future = useMemo(
    () =>
      tournaments
        .filter(
          (t) =>
            isFutureDay(t.startDate ?? t.StartDate, todayRef) &&
            !(t.isFinished ?? t.IsFinished)
        )
        .sort(
          (a, b) =>
            new Date(a.startDate ?? a.StartDate).getTime() -
            new Date(b.startDate ?? b.StartDate).getTime()
        ),
    [tournaments, todayRef]
  );

  const [extras, setExtras] = useState({});

  useEffect(() => {
    let cancelled = false;
    async function fetchExtras() {
      const all = [...live, ...future];
      if (all.length === 0) {
        if (!cancelled) setExtras({});
        return;
      }
      const entries = await Promise.all(
        all.map(async (t) => {
          const slug = pubField(t, "slug", "Slug");
          if (!slug) return [t.id, null];
          const isLive = live.some((l) => l.id === t.id);
          try {
            const requests = [
              axios.get(
                `${appConstants.API_URL}/api/public/tournaments/${slug}`
              ),
            ];
            if (isLive) {
              requests.push(
                axios.get(
                  `${appConstants.API_URL}/api/public/tournaments/${slug}/matches`,
                  { params: { completed: false } }
                )
              );
            }
            const results = await Promise.allSettled(requests);
            const detail =
              results[0].status === "fulfilled" ? results[0].value.data : null;
            const matches =
              isLive && results[1]?.status === "fulfilled"
                ? results[1].value.data ?? []
                : [];
            const activeMatch = isLive ? pickActiveMatch(matches) : null;
            return [
              t.id,
              { detail, activeMatch, stage: deriveStage(detail) },
            ];
          } catch {
            return [t.id, null];
          }
        })
      );
      if (cancelled) return;
      const map = {};
      entries.forEach(([k, v]) => {
        if (v) map[k] = v;
      });
      setExtras(map);
    }
    fetchExtras();
    return () => {
      cancelled = true;
    };
  }, [live, future]);

  const [featured, ...restLive] = live;

  if (live.length === 0 && future.length === 0) return emptyState;

  return (
    <div className="flex flex-col gap-12">
      {live.length > 0 ? (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-red-600">
            <LivePill />
            <span className="text-red-700/90">
              {live.length} en vivo · {live.length} activo
              {live.length === 1 ? "" : "s"}
            </span>
          </div>

          {featured ? (
            <FeaturedLiveCard
              tournament={featured}
              extras={extras[featured.id]}
              user={user}
              favorite={isFavorite ? isFavorite(featured) : false}
              onFavorite={onFavorite}
            />
          ) : null}

          {restLive.length > 0 ? (
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {restLive.map((t) => (
                <CompactTournamentCard
                  key={t.id}
                  tournament={t}
                  extras={extras[t.id]}
                  user={user}
                  favorite={isFavorite ? isFavorite(t) : false}
                  onFavorite={onFavorite}
                  variant="live"
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {future.length > 0 ? (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-arena">
            <UpcomingPill />
            <span className="text-arena/80">
              {future.length} próximo{future.length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {future.map((t) => (
              <CompactTournamentCard
                key={t.id}
                tournament={t}
                extras={extras[t.id]}
                user={user}
                favorite={isFavorite ? isFavorite(t) : false}
                onFavorite={onFavorite}
                variant="future"
                todayRef={todayRef}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
